// virtual-medic-frontend/src/components/Admin/BuildManagement.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';
import api from '../../services/api';

const BuildManagement = () => {
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkForm, setLinkForm] = useState({ id: '', platform: '', externalDownloadUrl: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadForm, setUploadForm] = useState({
    platform: '',
    version: '',
    description: '',
    systemRequirements: '',
    releaseNotes: '',
    azureBlobPath: '',
    externalDownloadUrl: '',
    file: null
  });

  useEffect(() => {
    fetchBuilds();
  }, []);

  const fetchBuilds = async () => {
    try {
      const response = await api.get('/admin/builds');
      setBuilds(response.data);
    } catch (error) {
      showSnackbar('Failed to fetch builds', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    const trimmedExternalUrl = uploadForm.externalDownloadUrl.trim();
    const hasFile = Boolean(uploadForm.file);
    const hasExternalLink = trimmedExternalUrl.length > 0;

    if (!uploadForm.platform || !uploadForm.version || (!hasFile && !hasExternalLink)) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setUploadLoading(true);
    
    const formData = new FormData();
    if (uploadForm.file) {
      formData.append('buildFile', uploadForm.file);
    }
    formData.append('platform', uploadForm.platform);
    formData.append('version', uploadForm.version);
    formData.append('description', uploadForm.description);
    formData.append('systemRequirements', uploadForm.systemRequirements);
    formData.append('releaseNotes', uploadForm.releaseNotes);
    formData.append('azureBlobPath', uploadForm.azureBlobPath.trim());
    formData.append('externalDownloadUrl', trimmedExternalUrl);

    try {
      await api.post('/admin/builds', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      showSnackbar('Build uploaded successfully', 'success');
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchBuilds();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to upload build', 'error');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleToggleStatus = async (buildId) => {
    try {
      await api.patch(`/admin/builds/${buildId}/toggle`);
      showSnackbar('Build status updated successfully', 'success');
      fetchBuilds();
    } catch (error) {
      showSnackbar('Failed to update build status', 'error');
    }
  };

  const handleDeleteBuild = async (buildId) => {
    if (!window.confirm('Are you sure you want to delete this build? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/builds/${buildId}`);
      showSnackbar('Build deleted successfully', 'success');
      fetchBuilds();
    } catch (error) {
      showSnackbar('Failed to delete build', 'error');
    }
  };

  const handleDownload = async (build) => {
    try {
      if (build.externalDownloadUrl) {
        window.open(build.externalDownloadUrl, '_blank', 'noopener');
        return;
      }

      const token = localStorage.getItem('token');
      const downloadUrl = `${process.env.REACT_APP_API_URL || 'https://virtualmedic.uk/api'}/admin/builds/${build._id}/download?token=${token}`;
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', build.originalFilename || 'build');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showSnackbar('Failed to download build', 'error');
    }
  };

  const openLinkDialog = (build) => {
    setLinkForm({
      id: build._id,
      platform: build.platform,
      externalDownloadUrl: build.externalDownloadUrl || ''
    });
    setLinkDialogOpen(true);
  };

  const handleLinkSave = async () => {
    if (!linkForm.id) {
      return;
    }

    setLinkSaving(true);
    try {
      await api.patch(`/admin/builds/${linkForm.id}/external-link`, {
        externalDownloadUrl: linkForm.externalDownloadUrl
      });
      showSnackbar('Download link updated successfully', 'success');
      setLinkDialogOpen(false);
      fetchBuilds();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to update download link', 'error');
    } finally {
      setLinkSaving(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      platform: '',
      version: '',
      description: '',
      systemRequirements: '',
      releaseNotes: '',
      azureBlobPath: '',
      externalDownloadUrl: '',
      file: null
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes)) return '-';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getPlatformColor = (platform) => {
    const colors = {
      windows: '#0078D4',
      macos: '#000000',
      ios: '#000000',
      android: '#3DDC84'
    };
    return colors[platform] || '#666666';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5">
          Application Builds
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{ 
            bgcolor: '#16a085',
            '&:hover': { bgcolor: '#1abc9c' }
          }}
        >
          Upload New Build
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Upload and manage application builds for different platforms. Only one build per platform can be active at a time.
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Platform</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Filename</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>Download Link</TableCell>
                <TableCell>Downloads</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {builds.map((build) => (
                <TableRow key={build._id}>
                  <TableCell>
                    <Chip
                      label={build.platform.toUpperCase()}
                      sx={{
                        bgcolor: getPlatformColor(build.platform),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>{build.version}</TableCell>
                  <TableCell>{build.originalFilename || (build.externalDownloadUrl ? 'External link' : '-')}</TableCell>
                  <TableCell>{formatFileSize(build.fileSize)}</TableCell>
                  <TableCell>
                    {new Date(build.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={build.externalDownloadUrl ? 'Linked' : 'Not set'}
                      color={build.externalDownloadUrl ? 'success' : 'default'}
                      icon={<LinkIcon />}
                      size="small"
                      variant={build.externalDownloadUrl ? 'filled' : 'outlined'}
                      onClick={() => openLinkDialog(build)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>{build.downloadCount}</TableCell>
                  <TableCell>
                    <Chip
                      label={build.isActive ? 'Active' : 'Inactive'}
                      color={build.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={() => handleDownload(build)}
                        title="Download"
                        size="small"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleToggleStatus(build._id)}
                        title={build.isActive ? 'Deactivate' : 'Activate'}
                        size="small"
                      >
                        {build.isActive ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteBuild(build._id)}
                        title="Delete"
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {builds.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      No builds uploaded yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload your first build to get started
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleUploadSubmit}>
          <DialogTitle>Upload New Build</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={uploadForm.platform}
                    label="Platform"
                    onChange={(e) => setUploadForm(prev => ({ ...prev, platform: e.target.value }))}
                  >
                    <MenuItem value="windows">Windows</MenuItem>
                    <MenuItem value="macos">macOS</MenuItem>
                    <MenuItem value="ios">iOS</MenuItem>
                    <MenuItem value="android">Android</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Version"
                  value={uploadForm.version}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, version: e.target.value }))}
                  placeholder="e.g., 1.0.0"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Choose Build File
                  <input
                    type="file"
                    hidden
                    accept=".exe,.dmg,.ipa,.apk,.msi,.deb,.rpm,.zip"
                    onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))}
                  />
                </Button>
                {uploadForm.file && (
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                  </Typography>
                )}
                <FormHelperText>
                  Upload a file or provide an external download link. Accepted formats: .exe, .dmg, .pkg, .ipa, .apk, .msi, .deb, .rpm, .zip (Max: 1GB). For macOS zip builds, create the zip on a Mac first and upload that exact zip without re-packing it on Windows.
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this build"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Azure Blob Path Override"
                  value={uploadForm.azureBlobPath}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, azureBlobPath: e.target.value }))}
                  placeholder="downloads/builds/macos/0.2.1/virtual-medic-macos-v0.2.1.zip"
                  helperText="Optional. Leave blank for automatic Azure placement when server-side Azure build storage is configured. Works for Windows and macOS. Example Windows path: downloads/builds/windows/0.2.0/virtual-medic-windows-v0.2.0.zip"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="External Download Link"
                  value={uploadForm.externalDownloadUrl}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, externalDownloadUrl: e.target.value }))}
                  placeholder="https://example.com/download"
                  helperText="Optional. Use a full http(s) URL to redirect downloads. Leave this blank if you want the uploaded file to be copied directly to Azure."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="System Requirements"
                  multiline
                  rows={2}
                  value={uploadForm.systemRequirements}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, systemRequirements: e.target.value }))}
                  placeholder="e.g., Windows 10 or higher, 4GB RAM, 500MB available space"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Release Notes"
                  multiline
                  rows={3}
                  value={uploadForm.releaseNotes}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, releaseNotes: e.target.value }))}
                  placeholder="What's new in this version?"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={uploadLoading}
              sx={{ 
                bgcolor: '#16a085',
                '&:hover': { bgcolor: '#1abc9c' }
              }}
            >
              {uploadLoading ? <CircularProgress size={24} /> : 'Upload Build'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set download link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="External download link"
            value={linkForm.externalDownloadUrl}
            onChange={(e) => setLinkForm(prev => ({ ...prev, externalDownloadUrl: e.target.value }))}
            placeholder="https://example.com/download"
            helperText="Leave blank to clear the link."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkSave}
            disabled={linkSaving}
            sx={{
              bgcolor: '#16a085',
              '&:hover': { bgcolor: '#1abc9c' }
            }}
          >
            {linkSaving ? <CircularProgress size={24} /> : 'Save Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BuildManagement;
